import { z } from 'zod';

// Helper to check if text is a placeholder
const isPlaceholder = (text: string) => {
  return text.startsWith('[') && text.endsWith(']');
};

// Hero Content Schema
const heroContentSchema = z
  .object({
    backgroundImage: z.string().optional(),
    backgroundVideo: z.string().optional(),
    logo: z.string().min(1, 'Company logo is required'),
    headline: z.string().min(1, 'Headline is required'),
    tagline: z.string().min(1, 'Tagline is required'),
    stats: z.array(
      z.object({
        value: z.string().min(1),
        label: z.string().min(1),
      })
    ),
  })
  .superRefine((data, ctx) => {
    // Require either background image or video
    const hasBackgroundImage = data.backgroundImage && data.backgroundImage.trim() !== '';
    const hasBackgroundVideo = data.backgroundVideo && data.backgroundVideo.trim() !== '';
    if (!hasBackgroundImage && !hasBackgroundVideo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Hero background image or video is required',
        path: ['backgroundImage'],
      });
    }
    if (isPlaceholder(data.headline)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please replace the placeholder headline with your actual company name',
        path: ['headline'],
      });
    }
    if (isPlaceholder(data.tagline)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please replace the placeholder tagline with your actual tagline',
        path: ['tagline'],
      });
    }

    // Check if at least one stat is completed (not a placeholder)
    const completedStats = data.stats.filter(
      (stat) => !isPlaceholder(stat.value) && !isPlaceholder(stat.label)
    );
    if (completedStats.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please complete at least one stat',
        path: ['stats'],
      });
    }
  });

// About Content Schema
const aboutContentSchema = z
  .object({
    teamPhoto: z.string().optional(),
    teamVideo: z.string().optional(),
    companyName: z.string().min(1, 'Company name is required'),
    bio: z.string().min(50, 'Bio must be at least 50 characters'),
  })
  .superRefine((data, ctx) => {
    // Require either team photo or video
    const hasTeamPhoto = data.teamPhoto && data.teamPhoto.trim() !== '';
    const hasTeamVideo = data.teamVideo && data.teamVideo.trim() !== '';
    if (!hasTeamPhoto && !hasTeamVideo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Team photo or video is required',
        path: ['teamPhoto'],
      });
    }
    if (isPlaceholder(data.companyName)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please replace the placeholder with your actual team name',
        path: ['companyName'],
      });
    }
    if (isPlaceholder(data.bio)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please replace the placeholder with your actual story/bio',
        path: ['bio'],
      });
    }
  });

// Feature Location Content Schema
const featureLocationContentSchema = z
  .object({
    locations: z.array(
      z.object({
        image: z.string(),
        title: z.string(),
      })
    ),
  })
  .superRefine((data, ctx) => {
    // Check if at least one location is completed (has image and non-placeholder title)
    const completedLocations = data.locations.filter(
      (loc) => loc.image && !isPlaceholder(loc.title)
    );
    if (completedLocations.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please complete at least one featured location',
        path: ['locations'],
      });
    }
  });

// Testimonials Content Schema
const testimonialsContentSchema = z
  .object({
    headerTagline: z.string().min(1, 'Header tagline is required'),
    sectionTitle: z.string().min(1, 'Section title is required'),
    testimonials: z.array(
      z.object({
        name: z.string(),
        role: z.string(),
        text: z.string(),
        rating: z.number().min(1).max(5),
      })
    ),
    googleReviewsLink: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val.trim() === '') return true; // Allow empty
          // Allow URLs with or without protocol
          try {
            const urlString =
              val.startsWith('http://') || val.startsWith('https://') ? val : `https://${val}`;
            new URL(urlString);
            return true;
          } catch {
            return false;
          }
        },
        { message: 'Invalid url (e.g., sample.com or https://sample.com)' }
      ),
    useGoogleReviews: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    // If Google Reviews link is provided or useGoogleReviews is true, testimonials are optional
    const hasGoogleReviews =
      data.useGoogleReviews || (data.googleReviewsLink && data.googleReviewsLink.trim() !== '');

    if (!hasGoogleReviews) {
      // Only require testimonials if Google Reviews is not being used
      const completedTestimonials = data.testimonials.filter(
        (t) =>
          !isPlaceholder(t.name) &&
          !isPlaceholder(t.role) &&
          !isPlaceholder(t.text) &&
          t.text.trim().length > 0
      );
      if (completedTestimonials.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Please complete at least one client testimonial or provide a Google Reviews link',
          path: ['testimonials'],
        });
      }
    }
  });

// Meet The Team Content Schema
const meetTheTeamContentSchema = z
  .object({
    headerTagline: z.string().min(1, 'Header tagline is required'),
    sectionTitle: z.string().min(1, 'Section title is required'),
    teamRosterLink: z.string().optional(),
    useTeamRosterLink: z.boolean().optional(),
    teamMembers: z.array(
      z.object({
        headshot: z.string(),
        name: z.string(),
        position: z.string(),
        phone: z.string(),
        email: z.string(),
        license: z.string().optional(),
        bio: z.string(),
      })
    ),
  })
  .superRefine((data, ctx) => {
    // If using team roster link, require the link to be filled
    if (data.useTeamRosterLink) {
      if (!data.teamRosterLink || data.teamRosterLink.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Team roster link is required when using this option',
          path: ['teamRosterLink'],
        });
      }
      // Skip team member validation since they're using a link
      return;
    }

    // If not using roster link, check for at least one completed team member
    const completedMembers = data.teamMembers.filter(
      (m) => m.headshot && m.name && !isPlaceholder(m.name)
    );
    if (completedMembers.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Please complete at least one team member (headshot photo and name required) or provide a team roster link',
        path: ['teamMembers'],
      });
    }
  });

// Footer Content Schema
const footerContentSchema = z
  .object({
    socialLinks: z
      .array(
        z.object({
          platform: z.string(),
          url: z.string().min(1),
        })
      )
      .optional(),
    contactInfo: z.object({
      phone: z.string().min(10, 'Valid phone number is required'),
      email: z.string().email('Valid email is required'),
      address: z.string().min(10, 'Complete address is required'),
    }),
    navigationLinks: z.array(
      z.object({
        label: z.string(),
        url: z.string(),
      })
    ),
    copyrightText: z.string().min(1, 'Copyright text is required'),
    brokerageLogo: z.string().min(1, 'Brokerage logo is required'),
    brokerageName: z.string().min(1, 'Brokerage name is required'),
  })
  .superRefine((data, ctx) => {
    // Validate social links are not placeholders
    data.socialLinks?.forEach((link, index) => {
      if (isPlaceholder(link.url)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Please replace placeholder URL for ${link.platform} or remove this social link`,
          path: ['socialLinks', index, 'url'],
        });
      }
    });

    // Validate contact info
    if (isPlaceholder(data.contactInfo.phone)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please replace placeholder with actual phone number',
        path: ['contactInfo', 'phone'],
      });
    }
    if (isPlaceholder(data.contactInfo.email)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please replace placeholder with actual email address',
        path: ['contactInfo', 'email'],
      });
    }
    if (isPlaceholder(data.contactInfo.address)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please replace placeholder with actual business address',
        path: ['contactInfo', 'address'],
      });
    }
    if (isPlaceholder(data.copyrightText)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please replace placeholder with actual company name in copyright',
        path: ['copyrightText'],
      });
    }
    // Validate brokerage name is not placeholder
    if (isPlaceholder(data.brokerageName)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please replace placeholder with actual brokerage name',
        path: ['brokerageName'],
      });
    }
  });

// Main Builder Content Schema
export const builderContentSchema = z.object({
  hero: heroContentSchema,
  about: aboutContentSchema,
  featureLocation: featureLocationContentSchema,
  testimonials: testimonialsContentSchema,
  meetTheTeam: meetTheTeamContentSchema,
  footer: footerContentSchema,
  accentColor: z.string().regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, 'Must be a valid hex color'),
});

export type BuilderContentData = z.infer<typeof builderContentSchema>;
